/**
 * useLiveQuizNavigation Hook
 * 
 * Manages quiz question navigation with bounds checking.
 * Provides navigation handlers and computed navigation state.
 */

import { useCallback, useMemo } from 'react';

/**
 * @typedef {Object} NavigationState
 * @property {number} currentIndex - Current question index (-1 = lobby)
 * @property {number} totalQuestions - Total question count
 * @property {boolean} isInLobby - Currently in lobby
 * @property {boolean} canGoFirst - Can navigate to first question
 * @property {boolean} canGoPrevious - Can go to previous question
 * @property {boolean} canGoNext - Can go to next question
 * @property {boolean} canGoLast - Can navigate to last question
 */

/**
 * @typedef {Object} NavigationActions
 * @property {Function} goFirst - Go to first question
 * @property {Function} goPrevious - Go to previous question
 * @property {Function} goNext - Go to next question
 * @property {Function} goLast - Go to last question
 * @property {Function} goToLobby - Return to lobby
 * @property {Function} goToQuestion - Go to specific question index
 */

/**
 * Hook to manage quiz navigation
 * 
 * @param {Object} options
 * @param {number} options.currentIndex - Current question index
 * @param {number} options.totalQuestions - Total number of questions
 * @param {Function} options.onNavigate - Called with new index when navigating
 * @param {Function} options.onBeforeNext - Called before navigating to next (for auto-award)
 * @returns {NavigationState & NavigationActions}
 */
export function useLiveQuizNavigation({
  currentIndex = -1,
  totalQuestions = 0,
  onNavigate,
  onBeforeNext
}) {
  // ============================================
  // COMPUTED STATE
  // ============================================
  
  const navigationState = useMemo(() => ({
    currentIndex,
    totalQuestions,
    isInLobby: currentIndex < 0,
    isOnFirstQuestion: currentIndex === 0,
    isOnLastQuestion: currentIndex === totalQuestions - 1,
    canGoFirst: currentIndex > 0,
    canGoPrevious: currentIndex > 0,
    canGoNext: currentIndex < totalQuestions - 1 && currentIndex >= 0,
    canGoLast: currentIndex < totalQuestions - 1 && currentIndex >= 0,
    canStartQuiz: currentIndex < 0 && totalQuestions > 0,
    // Progress info
    questionNumber: currentIndex >= 0 ? currentIndex + 1 : 0,
    progressPercentage: totalQuestions > 0 
      ? Math.round(((currentIndex + 1) / totalQuestions) * 100)
      : 0
  }), [currentIndex, totalQuestions]);

  // ============================================
  // NAVIGATION HANDLERS
  // ============================================
  
  /**
   * Navigate to first question
   */
  const goFirst = useCallback(() => {
    if (currentIndex > 0 && onNavigate) {
      onNavigate(0);
    }
  }, [currentIndex, onNavigate]);

  /**
   * Navigate to previous question
   */
  const goPrevious = useCallback(() => {
    if (currentIndex > 0 && onNavigate) {
      onNavigate(currentIndex - 1);
    }
  }, [currentIndex, onNavigate]);

  /**
   * Navigate to next question
   * Calls onBeforeNext callback first (for auto-point-award)
   */
  const goNext = useCallback(async () => {
    if (currentIndex < totalQuestions - 1 && currentIndex >= 0) {
      // Call onBeforeNext callback first (e.g. to award points)
      if (onBeforeNext) {
        await onBeforeNext();
      }
      
      if (onNavigate) {
        onNavigate(currentIndex + 1);
      }
    }
  }, [currentIndex, totalQuestions, onNavigate, onBeforeNext]);

  /**
   * Navigate to last question
   */
  const goLast = useCallback(() => {
    if (totalQuestions > 0 && currentIndex < totalQuestions - 1 && onNavigate) {
      onNavigate(totalQuestions - 1);
    }
  }, [currentIndex, totalQuestions, onNavigate]);

  /**
   * Return to lobby
   */
  const goToLobby = useCallback(() => {
    if (onNavigate) {
      onNavigate(-1);
    }
  }, [onNavigate]);

  /**
   * Go to specific question
   */
  const goToQuestion = useCallback((index) => {
    if (index >= -1 && index < totalQuestions && onNavigate) {
      onNavigate(index);
    }
  }, [totalQuestions, onNavigate]);

  /**
   * Start quiz (go from lobby to first question)
   */
  const startQuiz = useCallback(() => {
    if (currentIndex < 0 && totalQuestions > 0 && onNavigate) {
      onNavigate(0);
    }
  }, [currentIndex, totalQuestions, onNavigate]);

  // ============================================
  // KEYBOARD SHORTCUTS HELPER
  // ============================================
  
  /**
   * Get keyboard shortcut handlers map
   * Can be used with useEffect for keyboard event handling
   */
  const keyboardHandlers = useMemo(() => ({
    ArrowLeft: goPrevious,
    ArrowRight: goNext,
    ArrowUp: goFirst,
    ArrowDown: goLast,
    Home: goFirst,
    End: goLast,
    Escape: goToLobby
  }), [goFirst, goPrevious, goNext, goLast, goToLobby]);

  // ============================================
  // RETURN
  // ============================================
  
  return {
    // State
    ...navigationState,
    
    // Actions
    goFirst,
    goPrevious,
    goNext,
    goLast,
    goToLobby,
    goToQuestion,
    startQuiz,
    
    // Helpers
    keyboardHandlers
  };
}

export default useLiveQuizNavigation;
